import React, { useMemo, useState } from "react";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import PropTypes from "prop-types";
import ValidationInputField from "../ValidationInputField";
import { noop } from "shared-frontend/functions/noop";
import { Alert } from "@yoast/ui-library";
import stripCommonWrongSubdirectories from "../../functions/stripCommonWrongSubdirectories";

const messages = defineMessages( {
	enterSiteUrl: {
		id: "sites.select.enterNewSiteUrl",
		defaultMessage: "Enter a new site URL",
	},
	validationFormatURL: {
		id: "validation.format.url",
		defaultMessage: "Please enter a valid URL. Remember to use http:// or https://.",
	},
	urlCommonWrongSubdirectoryWarning: {
		id: "install.urlCommonWrongSubdirectoryWarning",
		defaultMessage: `It appears you have included "wp-admin" or "wp-json" in your url. This is allowed
						but not necessary. We will save your site as: {link}.`,
	},
} );

/**
 * A component for specifying a new site url.
 *
 * @param {object} props The properties.
 *
 * @returns {React.JSX.Element} The rendered components.
 */
const AddNewSite = ( props ) => {
	let urlConstraints              = null;
	const [ url, setUrl ]           = useState( props.rawNewSiteInput );
	if ( url !== "" ) {
		urlConstraints = {
			url: {
				message: props.intl.formatMessage( messages.validationFormatURL ),
			},
		};
	}

	const sanitizedURL = useMemo( () => stripCommonWrongSubdirectories( url ), [ url ] );

	/**
	 * Updates the state if a new site url has been entered.
	 *
	 * @param {string} value The new url value.
	 * @param {string[]} errors Any validation errors that apply to the new site url.
	 * @param {boolean} validating Whether or not the new site value is still being validated.
	 *
	 * @returns {void}
	 */
	function onChange( value, errors, validating ) {
		setUrl( value );
		props.onUrlChange( value );
		if ( validating || errors.length > 0 ) {
			props.onValidUrl( null );
			return;
		}

		const newStrippedUrlResult = stripCommonWrongSubdirectories( value );
		props.onValidUrl( new URL( newStrippedUrlResult.url ) );
	}

	return <>
		<ValidationInputField
			type="url"
			id="add-site-input"
			label={ props.intl.formatMessage( messages.enterSiteUrl ) }
			constraint={ urlConstraints }
			placeholder="https://example.com"
			value={ url }
			onChange={ onChange }
			disabled={ props.disabled }
			trimWhiteSpace={ true }
		/>
		{ sanitizedURL.strippedSubdirectory && sanitizedURL.url &&
			<Alert variant="info">
				<FormattedMessage
					{ ...messages.urlCommonWrongSubdirectoryWarning }
					values={ { link: sanitizedURL.url, warning: sanitizedURL.strippedSubdirectory } }
				/>
			</Alert>
		}
	</>;
};

AddNewSite.propTypes = {
	intl: intlShape.isRequired,
	rawNewSiteInput: PropTypes.string,
	onUrlChange: PropTypes.func,
	onValidUrl: PropTypes.func,
	disabled: PropTypes.bool,
};

AddNewSite.defaultProps = {
	rawNewSiteInput: "",
	onUrlChange: noop,
	onValidUrl: noop,
	disabled: false,
};

export default injectIntl( AddNewSite );
